// ===== Core Data Types =====

export interface GeoEvent {
  id: string;
  type: EventType;
  title: string;
  description?: string;
  lat: number;
  lon: number;
  timestamp: string;
  source: string;
  severity: ThreatLevel;
  country?: string;
  countryCode?: string;
  url?: string;
  category?: string;
}

export type EventType =
  | 'conflict'
  | 'protest'
  | 'earthquake'
  | 'wildfire'
  | 'military'
  | 'cyber'
  | 'economic'
  | 'diplomatic'
  | 'disaster'
  | 'terrorism'
  | 'health'
  | 'infrastructure'
  | 'environmental'
  | 'crime'
  | 'tech'
  | 'news';

export type ThreatLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

export interface NewsItem {
  id: string;
  title: string;
  description?: string;
  source: string;
  url: string;
  publishedAt: string;
  category?: EventType;
  severity?: ThreatLevel;
  country?: string;
  countryCode?: string;
  lat?: number;
  lon?: number;
  imageUrl?: string;
  /** 출처 피드의 카테고리 (defense/wire/asia 등) — Event Timeline military 탭 분류용 */
  feedCategory?: string;
}

export interface Earthquake {
  id: string;
  magnitude: number;
  place: string;
  lat: number;
  lon: number;
  depth: number;
  timestamp: string;
  url: string;
  tsunami: boolean;
  felt?: number;
}

export interface Wildfire {
  id: string;
  lat: number;
  lon: number;
  brightness: number;
  confidence: number;
  frp: number; // Fire Radiative Power
  timestamp: string;
  satellite: string;
  country?: string;
}

export interface CountryRisk {
  code: string;
  name: string;
  nameKo?: string;
  score: number; // 0-100
  trend: 'escalating' | 'stable' | 'de-escalating';
  components: {
    baseline: number;
    unrest: number;
    security: number;
    infoVelocity: number;
  };
  eventCount: number;
  topThreats: string[];
}

export type MarketCategory = 'crypto' | 'forex' | 'commodity' | 'macro' | 'defense' | 'energy' | 'food' | 'equity' | 'bonds';

export interface MarketQuote {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: string;
  category?: MarketCategory;
  subCategory?: string;   // equity: 'us-mega' | 'us-index' | 'asia' | 'europe' | 'finance' | 'bond-etf'
  unit?: string;
  marketCap?: number;
  high52?: number;
  low52?: number;
}

// ===== Map Types =====

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

export interface MapLayer {
  id: string;
  label: string;
  labelKo: string;
  visible: boolean;
  color: string;
  icon?: string;
}

export type RegionPreset = 'global' | 'asia' | 'europe' | 'americas' | 'mena' | 'africa';

export const REGION_VIEWS: Record<RegionPreset, MapViewState> = {
  global: { longitude: 30, latitude: 20, zoom: 1.8, pitch: 0, bearing: 0 },
  asia: { longitude: 115, latitude: 30, zoom: 3.5, pitch: 0, bearing: 0 },
  europe: { longitude: 15, latitude: 50, zoom: 3.8, pitch: 0, bearing: 0 },
  americas: { longitude: -90, latitude: 20, zoom: 2.8, pitch: 0, bearing: 0 },
  mena: { longitude: 45, latitude: 28, zoom: 3.5, pitch: 0, bearing: 0 },
  africa: { longitude: 20, latitude: 0, zoom: 3, pitch: 0, bearing: 0 },
};

// ===== UI Types =====

export interface PanelConfig {
  id: string;
  title: string;
  titleKo: string;
  component: string;
  defaultWidth: number; // grid columns
  defaultHeight: number; // grid rows
  minWidth?: number;
  minHeight?: number;
  refreshInterval?: number; // ms
}

export interface DataFreshness {
  source: string;
  lastFetch: number;
  status: 'fresh' | 'stale' | 'error' | 'loading';
  nextRefresh?: number;
}

// ===== Military / Transport / Infrastructure Types =====

export interface Aircraft {
  id: string;         // icao24 hex
  callsign: string;
  country: string;    // 등록국 (등록번호 접두사에서 추론)
  lat: number;
  lon: number;
  /** 기압고도. ADS-B 원본 단위 = **피트**. (이전 이름 altitude, 주석이 meters 로 잘못돼 있었음) */
  altitudeFt: number;
  /** 대지속도. ADS-B 원본 단위 = **노트**. (이전 이름 velocity, 주석이 m/s 로 잘못돼 있었음) */
  speedKt: number;
  heading: number;    // degrees true north
  onGround: boolean;
  isMilitary: boolean;

  // ── 선택 필드 (opendata.adsb.fi 가 함께 주는 기체 정보) ──
  registration?: string;   // tail number, 예: N498MC
  acType?: string;         // ICAO 기종코드, 예: B744
  acDesc?: string;         // 예: BOEING 747-400
  operator?: string;       // 예: ATLAS AIR INC
  squawk?: string;
  verticalRateFpm?: number;

  // ── 노선 (adsbdb.com 에서 클릭 시 온디맨드 조회) ──
  route?: AircraftRoute;
}

/** adsbdb.com callsign 조회 결과 — ADS-B 전파에는 목적지가 없어 별도 DB 조회가 필요하다. */
export interface AircraftRoute {
  callsign: string;
  airline?: string;
  originIata?: string;
  originName?: string;
  originCity?: string;
  destIata?: string;
  destName?: string;
  destCity?: string;
}

export type ShipType = 'cargo' | 'tanker' | 'container' | 'passenger' | 'bulk' | 'other';

export interface Ship {
  id: string;
  name: string;
  type: ShipType;
  lat: number;
  lon: number;
  heading: number;  // degrees
  speed: number;    // knots (SOG)
  flag: string;     // country name
  flagCode: string; // ISO 3166-1 alpha-2

  // ── 실시간 AIS (aisstream.io) 전용 ──
  /** true = 실시간 AIS 수신. false/undefined = 시뮬레이션 항로. */
  isLive?: boolean;
  mmsi?: number;
  callSign?: string;
  /** AIS ShipStaticData 가 싣고 오는 선원 입력 목적지 (자유 텍스트) */
  destination?: string;
  /** 마지막 AIS 수신 시각 (epoch ms) */
  lastSeen?: number;
}

export type NavalType = 'carrier' | 'destroyer' | 'frigate' | 'submarine' | 'cruiser' | 'amphibious' | 'patrol';

export interface NavalVessel {
  id: string;
  name: string;
  type: NavalType;
  lat: number;
  lon: number;
  heading: number;
  country: string;
  countryCode: string;
  groupName?: string; // e.g. "CSG-8 Strike Group"
}

export interface FighterJet {
  id: string;
  callsign: string;
  aircraft: string;  // e.g. "F-35A", "Su-35", "J-20"
  lat: number;
  lon: number;
  heading: number;
  altitude: number;  // meters
  country: string;
  countryCode: string;
  mission: 'patrol' | 'combat' | 'intercept' | 'training' | 'strike';
}

export type PowerPlantType = 'nuclear' | 'coal' | 'gas' | 'hydro' | 'wind' | 'solar' | 'oil' | 'geothermal';

export interface PowerPlant {
  id: string;
  name: string;
  type: PowerPlantType;
  lat: number;
  lon: number;
  country: string;
  countryCode: string;
  capacityMw: number;
  status: 'operational' | 'offline' | 'reduced' | 'decommissioned';
}

export type ConflictType = 'active-war' | 'insurgency' | 'tension' | 'occupation' | 'siege';
export type ConflictStatus = 'active' | 'ceasefire' | 'ended' | 'negotiating';

export interface ConflictZone {
  id: string;
  name: string;
  type: ConflictType;
  lat: number;
  lon: number;
  radiusKm: number;
  severity: ThreatLevel;
  parties: string[];
  startDate: string;
  description: string;
  country?: string;
  status?: ConflictStatus;     // ceasefire / ended / negotiating
  ceasefireDate?: string;      // ISO date when ceasefire began
  ceasefireNotes?: string;     // brief notes, e.g. "Phase 1 deal, Jan 19 2025"
}

// ===== Crisis Alert Types =====

export type AlertLevel = 'warning' | 'critical' | 'extreme';

export interface CrisisAlert {
  id: string;
  level: AlertLevel;
  title: string;
  titleKo: string;
  region: string;
  lat?: number;          // map coordinate (from REGION_COORDS)
  lon?: number;
  summary: string;
  confidence: number;    // 0-100
  sources: string[];
  timestamp: string;
  relatedEventIds: string[];
  dismissed: boolean;
}

// ===== Creative Layer Types =====

export interface SubmarineCable {
  id: string;
  name: string;
  path: [number, number][];  // [lon, lat] pairs
  status: 'active' | 'disrupted' | 'cut';
  owner: string;
  capacity: string;          // e.g. "200 Tbps"
}

export interface DroneZone {
  id: string;
  name: string;
  lat: number;
  lon: number;
  radiusKm: number;
  type: 'active-combat' | 'patrol' | 'surveillance' | 'contested';
  country?: string;
  severity: ThreatLevel;
}

export interface NuclearAsset {
  id: string;
  name: string;
  country: string;
  countryCode: string;
  lat: number;
  lon: number;
  type: 'icbm-silo' | 'submarine-base' | 'air-base' | 'test-site' | 'storage';
  rangeKm?: number;  // missile range for visualization
}

// ===== Rich Tooltip (for hover overlays) =====

export interface TooltipNewsItem {
  title: string;
  source: string;
  publishedAt: string;
}

export interface RichTooltip {
  x: number;
  y: number;
  header: string;
  subheader?: string;
  details: Array<{ label: string; value: string; highlight?: boolean }>;
  newsItems?: TooltipNewsItem[];
  badgeText?: string;
  badgeColor?: 'red' | 'orange' | 'yellow' | 'blue' | 'green';
}

// ===== Flight Routes =====

export interface FlightRoute {
  id: string;
  flightNumber: string;
  airline: string;
  originIata: string;
  originName: string;
  originLon: number;
  originLat: number;
  destIata: string;
  destName: string;
  destLon: number;
  destLat: number;
  currentLon: number;
  currentLat: number;
  departureTime: string;   // ISO string
  arrivalTime: string;     // ISO string
  progress: number;        // 0-1
  altitude: number;        // meters
}

// ===== Troop Movements =====

export type MovementType = 'ground' | 'naval' | 'air' | 'logistics';
export type MovementScale = 'company' | 'battalion' | 'brigade' | 'division' | 'corps';

export interface TroopMovement {
  id: string;
  fromCountry: string;
  fromLon: number;
  fromLat: number;
  toCountry: string;
  toLon: number;
  toLat: number;
  type: MovementType;
  scale: MovementScale;
  description: string;
  relatedNews: TooltipNewsItem[];
  startDate: string;
  severity: ThreatLevel;
}

// ===== Missile Trajectories =====

export type MissileType = 'ballistic' | 'cruise' | 'hypersonic' | 'drone';
export type MissileStatus = 'flying' | 'impact' | 'cooldown';

export interface MissileTrajectory {
  id: string;
  type: MissileType;
  originLon: number;
  originLat: number;
  targetLon: number;
  targetLat: number;
  firedBy: string;
  targetName: string;
  flightTimeMs: number;
  startedAt: number;       // ms timestamp
  status: MissileStatus;
  impactAt?: number;
  cooldownUntil?: number;
  relatedNews: TooltipNewsItem[];
  progress: number;        // 0-1 computed by tickMissiles
  currentLon: number;
  currentLat: number;
}

// ===== Audience Profiles =====

export type AudienceProfile = 'INTEL' | 'MEDIA' | 'FINANCE' | 'INFRA' | 'PUBLIC';

export const AUDIENCE_LAYER_PRESETS: Record<AudienceProfile, string[]> = {
  INTEL: ['war-zones', 'conflicts', 'fighters', 'naval', 'carriers', 'drone-zones', 'nuclear', 'troop-movements', 'missiles', 'drone-attacks', 'cyber-threats', 'chokepoints', 'cities'],
  MEDIA: ['conflicts', 'earthquakes', 'wildfires', 'news', 'war-zones', 'troop-movements', 'missiles', 'drone-attacks', 'cities'],
  FINANCE: ['earthquakes', 'wildfires', 'news', 'ships', 'submarine-cables', 'power-plants', 'cyber-threats', 'chokepoints', 'cities'],
  INFRA: ['power-plants', 'submarine-cables', 'ships', 'naval', 'earthquakes', 'cyber-threats', 'chokepoints'],
  PUBLIC: ['conflicts', 'earthquakes', 'wildfires', 'news', 'war-zones', 'cities'],
};

// ===== Cyber Threat Intelligence (Recorded Future style) =====

export type CyberAttackType =
  | 'ransomware'
  | 'ddos'
  | 'apt'
  | 'data-breach'
  | 'infrastructure'
  | 'espionage'
  | 'disinformation'
  | 'supply-chain';

export interface CyberThreat {
  id: string;
  name: string;
  type: CyberAttackType;
  threatActor: string;
  originCountry: string;
  originLon: number;
  originLat: number;
  targetCountry: string;
  targetSector: string;    // e.g. 'Finance', 'Energy', 'Government'
  targetLon: number;
  targetLat: number;
  severity: ThreatLevel;
  timestamp: string;
  description: string;
  affectedSectors: string[];
  iocs: string[];          // Indicators of Compromise (IPs, domains, hashes)
  cveIds?: string[];       // CVE identifiers
  active: boolean;
}

// ===== Social Signal Intelligence (Dataminr style) =====

export type SocialPlatform = 'twitter' | 'telegram' | 'reddit' | 'weibo' | 'vk' | 'signal';

export interface SocialSignal {
  id: string;
  platform: SocialPlatform;
  text: string;
  author: string;
  confidence: number;    // 0-100
  severity: ThreatLevel;
  lat?: number;
  lon?: number;
  country?: string;
  countryCode?: string;
  timestamp: string;
  verifiedAt?: string;
  keywords: string[];
  category: EventType;
  isFirstReport: boolean;  // True = before mainstream media
  engagementScore: number; // views/shares/retweets
}

// ===== Economic Calendar (Bloomberg style) =====

export type EconomicEventCategory =
  | 'interest-rate'
  | 'gdp'
  | 'inflation'
  | 'employment'
  | 'trade'
  | 'pmi'
  | 'sentiment'
  | 'sanctions'
  | 'election';

export interface EconomicEvent {
  id: string;
  title: string;
  country: string;
  countryCode: string;
  date: string;          // YYYY-MM-DD
  time: string;          // HH:MM UTC
  importance: 'high' | 'medium' | 'low';
  previous?: string;
  forecast?: string;
  actual?: string;
  category: EconomicEventCategory;
  impact?: 'bullish' | 'bearish' | 'neutral';
}

export interface BondYield {
  country: string;
  countryCode: string;
  maturity: string;      // '2Y', '10Y', '30Y'
  yield: number;
  change: number;
  timestamp: string;
}

export interface CreditSpread {
  name: string;          // 'IG', 'HY', 'EM'
  spread: number;        // basis points
  change: number;
  description: string;
}

// ===== Drone Attack Trajectories =====

export type DroneType = 'fpv' | 'loitering' | 'surveillance' | 'naval' | 'shahed' | 'switchblade';
export type DroneStatus = 'inbound' | 'impact' | 'intercepted' | 'cooldown';

export interface DroneAttack {
  id: string;
  type: DroneType;
  originLon: number;
  originLat: number;
  targetLon: number;
  targetLat: number;
  firedBy: string;
  targetName: string;
  flightTimeMs: number;
  cycleOffsetMs: number;
  cooldownMs: number;
  status: DroneStatus;
  progress: number;         // 0-1 computed by tickDrones
  currentLon: number;
  currentLat: number;
  intercepted?: boolean;
  relatedNews: TooltipNewsItem[];
  snsSources: string[];     // platform references (Telegram channel, Twitter account)
}

// ===== World City Intelligence =====

export interface WorldCity {
  id: string;
  name: string;
  nameKo?: string;
  country: string;
  countryCode: string;
  population: number;          // millions
  lat: number;
  lon: number;
  isCapital: boolean;
  strategicImportance: 'critical' | 'high' | 'medium' | 'low';
  notes?: string;
}

// ===== Country Intelligence Profile =====

export type GeopoliticalStance = 'western' | 'eastern' | 'neutral' | 'revisionist' | 'rogue' | 'pivotal';
export type NuclearStatus = 'none' | 'possesses' | 'pursuing' | 'umbrella';

export interface CountryLeader {
  name: string;
  nameKo?: string;
  title: string;
  sinceYear: number;
  age: number;
  riskLevel: ThreatLevel;       // leader stability risk
  riskNotes: string;
  successionRisk: ThreatLevel;
  ideology?: string;             // e.g. "MAGA nationalist", "Marxist-Leninist"
  orientation?: 'far-right' | 'right' | 'center-right' | 'center' | 'center-left' | 'left' | 'far-left' | 'authoritarian' | 'theocratic';
}

// ===== Country Rivalry / Bilateral Tension =====

export type RivalryType = 'war' | 'territorial' | 'sanctions' | 'proxy-war' | 'economic' | 'ideological' | 'nuclear' | 'cyber';

export interface CountryRivalry {
  id: string;
  country1: string;             // ISO code
  country2: string;             // ISO code
  country1Name: string;
  country2Name: string;
  type: RivalryType[];
  intensity: ThreatLevel;
  description: string;
  since: string;                // year or date
  activeMilitary: boolean;
  keyIssues: string[];
  latestIntel?: string;
  simulationEnabled?: boolean;  // paid feature flag
}

export interface MilitaryStrength {
  activeTroops: number;         // thousands
  reserveTroops: number;        // thousands
  aircraftCarriers: number;
  submarines: number;
  destroyersFrigates: number;
  combatAircraft: number;
  tanks: number;
  artilleryPieces: number;
  navalVesselsTotal: number;
  nuclearWarheads?: number;
  budgetBillionUSD: number;
  globalFirepowerRank?: number;
}

export interface CountryProfile {
  code: string;                 // ISO 3166-1 alpha-2
  name: string;
  nameKo?: string;
  capitalCity: string;
  capitalLon: number;
  capitalLat: number;
  population: number;           // millions
  gdpBillionUSD: number;
  gdpPerCapita: number;
  gdpGrowthPct: number;
  gdpSectors?: { name: string; pct: number }[];  // e.g. [{name:'Services', pct:80}]
  military: MilitaryStrength;
  nuclearStatus: NuclearStatus;
  leader: CountryLeader;
  alliances: string[];          // ['NATO', 'G7', 'G20', 'SCO', 'QUAD', 'AUKUS', etc.]
  geopoliticalStance: GeopoliticalStance;
  strategicObjectives: string[];
  keyRivalries?: string[];       // ISO codes of rival countries
  instabilityRisk: ThreatLevel;
  powerIndex: number;           // 0-100 composite score
  flagEmoji: string;
  infrastructure?: {
    internetPenetration: number;  // %
    electricityAccess: number;    // %
    railwayKm: number;
    highwayKm: number;
    topPorts: string[];
    topAirports: string[];
  };
  // REST Countries fallback metadata (only present when source === 'rest-countries')
  source?: 'static' | 'rest-countries';
  _region?: string;
  _languages?: string;
  _currencies?: string;
  _area?: number;          // km²
  _borders?: string[];     // ISO-3 codes of bordering countries
}

// ===== Strategic Chokepoint =====

export interface StrategicChokepoint {
  id: string;
  name: string;
  nameKo?: string;
  lon: number;
  lat: number;
  type: 'strait' | 'canal' | 'passage' | 'hub';
  dailyShips: number;           // vessels per day
  oilMbdEquivalent?: number;    // million barrels/day equivalent
  threatLevel: ThreatLevel;
  controlledBy: string[];
  strategicNotes: string;
}

// ===== Carrier Group =====

export interface CarrierGroup {
  id: string;
  carrierName: string;
  country: string;
  countryCode: string;
  lat: number;
  lon: number;
  heading: number;
  groupName: string;
  airWing: string;              // e.g. "CVW-17"
  destroyers: string[];
  cruisers: string[];
  submarines: string[];
  supplyShips: string[];
  mission: string;
  region: string;
  threatLevel: ThreatLevel;
}

// ===== Sanctions Intelligence (Refinitiv style) =====

export type SanctionType = 'individual' | 'entity' | 'vessel' | 'aircraft' | 'country';

export interface SanctionEntry {
  id: string;
  name: string;
  nameKo?: string;
  country: string;
  countryCode: string;
  type: SanctionType;
  sanctionedBy: string[];   // ['OFAC', 'EU', 'UN', 'UK']
  reason: string;
  date: string;
  sector?: string;          // 'Energy', 'Finance', 'Defense'
  relatedEntities?: string[];
  active: boolean;
}

// ===== ACLED — Armed Conflict Location & Event Data =====

export type AcledEventType =
  | 'Battles'
  | 'Explosions/Remote violence'
  | 'Violence against civilians'
  | 'Protests'
  | 'Riots'
  | 'Strategic developments';

export interface AcledEvent {
  id: string;
  event_date: string;
  event_type: AcledEventType;
  sub_event_type: string;
  actor1: string;
  actor2: string;
  country: string;
  location: string;
  lat: number;
  lon: number;
  fatalities: number;
  notes: string;
  source: string;
  severity: ThreatLevel;
}

// ===== NOTAM — Notice to Air Missions =====

export type NotamType = 'D' | 'FDC' | 'INTL' | 'MIL';

export interface NotamItem {
  id: string;
  location: string;
  type: NotamType;
  text: string;
  lat: number;
  lon: number;
  radiusNm: number;
  altitudeMin?: string;
  altitudeMax?: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  category: 'restricted' | 'military' | 'tfrzone' | 'airshow' | 'other';
}
